import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || '';

const api = axios.create({
  baseURL: `${API_URL}/api/v1`,
  headers: { 'Content-Type': 'application/json' },
});

// Attach JWT token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('kog_access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 → redirect to login
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('kog_access_token');
      localStorage.removeItem('kog_refresh_token');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

export default api;

// ── Auth ─────────────────────────────────────────────────────────────

export async function login(username: string, password: string) {
  const res = await api.post('/auth/login', { username, password });
  const { access_token, refresh_token, user } = res.data;
  localStorage.setItem('kog_access_token', access_token);
  localStorage.setItem('kog_refresh_token', refresh_token);
  return user;
}

export async function register(data: {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  company_name: string;
}) {
  const res = await api.post('/auth/register', data);
  const { access_token, refresh_token } = res.data;
  localStorage.setItem('kog_access_token', access_token);
  localStorage.setItem('kog_refresh_token', refresh_token);
  return res.data.user;
}

export async function logout() {
  localStorage.removeItem('kog_access_token');
  localStorage.removeItem('kog_refresh_token');
}

export async function changePassword(currentPassword: string, newPassword: string) {
  const res = await api.post('/auth/change-password', {
    current_password: currentPassword,
    new_password: newPassword,
  });
  return res.data;
}

export async function getCurrentUser() {
  const res = await api.get('/auth/me');
  return res.data;
}

// ── MFA ──────────────────────────────────────────────────────────────

export async function getMFAStatus() {
  const res = await api.get('/auth/mfa/status');
  return res.data;
}

export async function setupMFA() {
  const res = await api.post('/auth/mfa/setup');
  return res.data;
}

export async function verifyMFA(code: string) {
  const res = await api.post('/auth/mfa/verify', { code });
  return res.data;
}

export async function disableMFA(password: string) {
  const res = await api.post('/auth/mfa/disable', { password });
  return res.data;
}

// ── Contacts ─────────────────────────────────────────────────────────

export async function getContacts(params?: Record<string, string>) {
  const res = await api.get('/contacts', { params });
  return res.data;
}

export async function getContact(id: string) {
  const res = await api.get(`/contacts/${id}`);
  return res.data;
}

export async function createContact(data: Record<string, unknown>) {
  const res = await api.post('/contacts', data);
  return res.data;
}

export async function updateContact(id: string, data: Record<string, unknown>) {
  const res = await api.put(`/contacts/${id}`, data);
  return res.data;
}

// ── Organizations ────────────────────────────────────────────────────

export async function getOrganizations(params?: Record<string, string>) {
  const res = await api.get('/organizations', { params });
  return res.data;
}

export async function getOrganization(id: string) {
  const res = await api.get(`/organizations/${id}`);
  return res.data;
}

// ── Deals ────────────────────────────────────────────────────────────

export async function getDeals(params?: Record<string, string>) {
  const res = await api.get('/opportunities', { params });
  return res.data;
}

export async function getDeal(id: string) {
  const res = await api.get(`/opportunities/${id}`);
  return res.data;
}

export async function updateDeal(id: string, data: Record<string, unknown>) {
  const res = await api.put(`/opportunities/${id}`, data);
  return res.data;
}

// ── Activities ───────────────────────────────────────────────────────

export async function getActivities(params?: Record<string, string>) {
  const res = await api.get('/activities', { params });
  return res.data;
}

export async function createActivity(data: Record<string, unknown>) {
  const res = await api.post('/activities', data);
  return res.data;
}

// ── Analytics ────────────────────────────────────────────────────────

export async function getDashboardMetrics() {
  const res = await api.get('/analytics/dashboard');
  return res.data;
}

// ── AI Insights ──────────────────────────────────────────────────────

export async function getLeadScore(contactId: string) {
  const res = await api.post('/ai-insights/lead-score', { contact_id: contactId });
  return res.data;
}

export async function generateEmailDraft(contactId: string, context?: string) {
  const res = await api.post('/ai-insights/email-draft', { contact_id: contactId, context });
  return res.data;
}

export async function getAISummary(entityId: string, entityType: string) {
  const res = await api.post('/ai-insights/summarize', { entity_id: entityId, entity_type: entityType });
  return res.data;
}

// ── Campaigns ────────────────────────────────────────────────────────

export async function getCampaigns(params?: Record<string, string>) {
  const res = await api.get('/campaigns/', { params });
  return res.data;
}

export async function getCampaign(id: string) {
  const res = await api.get(`/campaigns/${id}`);
  return res.data;
}

export async function createCampaign(data: Record<string, unknown>) {
  const res = await api.post('/campaigns/', data);
  return res.data;
}

export async function getCampaignContacts(campaignId: string) {
  const res = await api.get(`/campaigns/${campaignId}/contacts`);
  return res.data;
}

export async function removeContactFromCampaign(campaignId: string, contactId: string) {
  await api.delete(`/campaigns/${campaignId}/contacts/${contactId}`);
}

export async function addContactsToCampaign(campaignId: string, contactIds: string[]) {
  const res = await api.post(`/campaigns/${campaignId}/contacts/add`, { contact_ids: contactIds });
  return res.data;
}

export async function updateCampaignContactStatus(campaignId: string, contactId: string, status: string) {
  const res = await api.patch(`/campaigns/${campaignId}/contacts/${contactId}/status`, { status });
  return res.data;
}

export async function copyContactsToCampaign(sourceCampaignId: string, targetCampaignId: string, contactIds?: string[]) {
  const res = await api.post(`/campaigns/${sourceCampaignId}/contacts/copy-to/${targetCampaignId}`, contactIds ? { contact_ids: contactIds } : {});
  return res.data;
}

export async function scheduleMeeting(campaignId: string, contactId: string, data: Record<string, unknown>) {
  const res = await api.post(`/campaigns/${campaignId}/contacts/${contactId}/schedule-meeting`, data);
  return res.data;
}

// ── Campaign Intelligence ───────────────────────────────────────────

export async function getCampaignDashboard(campaignId: string) {
  const res = await api.get(`/campaigns/${campaignId}/intelligence/dashboard`);
  return res.data;
}

export async function rankProspects(campaignId: string) {
  const res = await api.post(`/campaigns/${campaignId}/intelligence/rank-prospects`, {});
  return res.data;
}

export async function generateDrafts(campaignId: string) {
  const res = await api.post(`/campaigns/${campaignId}/intelligence/generate-drafts`, {});
  return res.data;
}

// ── Documents (proxied through KOG API → charlotte-ingest) ──────────

export async function uploadDocument(file: File, collection: string = 'kog_sales') {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('collection', collection);
  const res = await api.post('/documents/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
  });
  return res.data;
}

export async function searchDocuments(query: string, collection: string = 'kog_sales') {
  const res = await api.post('/documents/search', { query, collection, n_results: 10 });
  return res.data;
}

export async function listDocuments(collection: string = 'kog_sales') {
  const res = await api.get('/documents/collections', { params: { collection } });
  return res.data;
}

// ── Products ────────────────────────────────────────────────────────

export async function getProducts(params?: Record<string, string>) {
  const res = await api.get('/products', { params });
  return res.data;
}

export async function getProduct(id: string) {
  const res = await api.get(`/products/${id}`);
  return res.data;
}

export async function createProduct(data: Record<string, unknown>) {
  const res = await api.post('/products', data);
  return res.data;
}

export async function updateProduct(id: string, data: Record<string, unknown>) {
  const res = await api.put(`/products/${id}`, data);
  return res.data;
}

export async function deleteProduct(id: string) {
  const res = await api.delete(`/products/${id}`);
  return res.data;
}

export async function getProductCategories() {
  const res = await api.get('/products/categories');
  return res.data;
}

// ── Stripe ──────────────────────────────────────────────────────────

export async function getStripeStatus() {
  const res = await api.get('/products/stripe/status');
  return res.data;
}

export async function connectStripe(apiKey: string) {
  const res = await api.put('/products/stripe/connect', { api_key: apiKey });
  return res.data;
}

export async function disconnectStripe() {
  const res = await api.delete('/products/stripe/disconnect');
  return res.data;
}

export async function previewStripeProducts() {
  const res = await api.get('/products/stripe/preview');
  return res.data;
}

export async function syncFromStripe() {
  const res = await api.post('/products/stripe/sync');
  return res.data;
}

export async function pushToStripe(productId: string) {
  const res = await api.post(`/products/${productId}/stripe/push`);
  return res.data;
}

export async function pushAllToStripe() {
  const res = await api.post('/products/stripe/push-all');
  return res.data;
}

export async function syncBidirectional() {
  const res = await api.post('/products/stripe/sync-bidirectional');
  return res.data;
}

// ── Billing ─────────────────────────────────────────────────────────

export async function getBillingPlans() {
  const res = await api.get('/billing/plans');
  return res.data;
}

export async function getSubscriptions() {
  const res = await api.get('/billing/subscriptions');
  return res.data;
}

export async function createCheckoutSession(planId: string) {
  const res = await api.post('/billing/checkout', { plan_id: planId });
  return res.data;
}

// ── Invoices ────────────────────────────────────────────────────────

export const getInvoices = (limit = 20) => api.get('/billing/invoices', { params: { limit } });
export const getInvoice = (id: string) => api.get(`/billing/invoices/${id}`);
export const createInvoice = (data: {
  customer_email?: string;
  line_items: Array<{ description: string; quantity: number; unit_amount: number; currency: string }>;
  due_days?: number;
  memo?: string;
  template_id?: string;
}) => api.post('/billing/invoices', data);
export const sendInvoice = (id: string) => api.post(`/billing/invoices/${id}/send`);
export const voidInvoice = (id: string) => api.post(`/billing/invoices/${id}/void`);

// ── Job Search ──────────────────────────────────────────────────────

export async function getJobApplications(params?: { status?: string; priority?: string; search?: string }) {
  const res = await api.get('/job-search/tracker', { params });
  return res.data;
}

export async function getJobApplication(id: string) {
  const res = await api.get(`/job-search/tracker/${id}`);
  return res.data;
}

export async function createJobApplication(data: Record<string, unknown>) {
  const res = await api.post('/job-search/tracker', data);
  return res.data;
}

export async function updateJobApplication(id: string, data: Record<string, unknown>) {
  const res = await api.put(`/job-search/tracker/${id}`, data);
  return res.data;
}

export async function deleteJobApplication(id: string) {
  await api.delete(`/job-search/tracker/${id}`);
}

export async function getJobSearchStats() {
  const res = await api.get('/job-search/tracker/stats');
  return res.data;
}

export async function triggerJobDiscovery(scriptName = 'evening') {
  const res = await api.post(`/job-search/scripts/${scriptName}`);
  return res.data;
}

export async function researchJobApplication(id: string) {
  const res = await api.post(`/job-search/${id}/research`);
  return res.data;
}

export async function generateInterviewPrep(id: string) {
  const res = await api.post(`/job-search/${id}/interview-prep`);
  return res.data;
}

// ── Usage Metering ──────────────────────────────────────────────────

export async function getUsage() {
  const res = await api.get('/usage');
  return res.data;
}

// ── Invoice Templates ────────────────────────────────────────────────

// ── Data Export ─────────────────────────────────────────────────────

export async function downloadExport(type: 'contacts' | 'organizations' | 'activities') {
  const res = await api.get(`/export/${type}.csv`, { responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement('a');
  a.href = url;
  a.download = `${type}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

// ── Invoice Templates ────────────────────────────────────────────────

export const getInvoiceTemplates = () => api.get('/billing/invoice-templates');
export const createInvoiceTemplate = (data: Record<string, unknown>) => api.post('/billing/invoice-templates', data);
export const createInvoiceFromTemplate = (
  templateId: string,
  overrides?: { quantity?: number; customer_email?: string; memo?: string }
) => api.post(`/billing/invoice-templates/${templateId}/create-invoice`, overrides);
